import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import React from "react";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { UI_CONTEXTS } from "@/analytics/constants";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import { getBuilderMessages, getBuilderNavLinks } from "@/pages/api/builder/builder";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";
import { fetchAllRetailerPages } from "@/pages/api/store-directory/stores";

import { RetailerTiles } from "@/components/retailer-tiles/retailer-tiles";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";
import { RetailerPageProps } from "@/interfaces/retailer-page-interface";
import { ThumbStoreProps } from "@/interfaces/stores-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

type LocaleProps = {
    locale: Locale;
};

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type GroupedRetailerProps = {
    groupedStores: Array<RetailerPageProps>;
};

type OwnProps = {
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & GroupedRetailerProps & LocaleProps;

init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale, resolvedUrl }) => {
    const { stores, ...parallelProps } = await parallel({
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        stores: () => fetchAllRetailerPages(locale),
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    const groupedStores = stores;

    return {
        props: {
            groupedStores,
            locale,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const PageContent = ({
    builderMessages,
    builderNavLinks,
    groupedStores,
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
}: Props): React.ReactNode => {
    const { t } = useTranslation();

    const stores: Array<ThumbStoreProps> = groupedStores
        ? groupedStores.map((_) => {
              return {
                  id: _.id,
                  name: _.name,
                  mainImageUrl: _.mainImageUrl,
                  merchantId: _.merchantId,
                  slug: _.slug,
                  newLogoUrl: _.newLogoUrl,
              };
          })
        : [];
    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <div>
                <h1 style={{ fontSize: "2.5rem", fontWeight: 600, marginTop: "2rem" }}>
                    {t("retailerShopList.title")}
                </h1>
                <p style={{ marginBottom: "3rem" }}>{t("retailerShopList.subtitle")}</p>
            </div>
            <RetailerTiles
                stores={stores}
                analytics={{
                    uiContext: UI_CONTEXTS.COLLECTION_TILE,
                }}
            />
        </DefaultLayout>
    );
};

export default PageContent;
